import React, { useEffect, useRef, useState } from 'react';
import { Modal } from './ui';

// Conferme e richieste di testo nello stile dell'app, al posto di window.confirm/prompt
// (che su Windows aprono la finestrina di sistema, chiara anche in tema scuro).

type Req =
  | { kind: 'confirm'; message: string; title: string; ok: string; cancel: string; danger: boolean; resolve: (v: boolean) => void }
  | { kind: 'prompt'; message: string; title: string; value: string; resolve: (v: string | null) => void };

let push: ((r: Req) => void) | null = null;
const queue: Req[] = [];

export interface AskOptions {
  title?: string;
  ok?: string;
  cancel?: string;
  danger?: boolean;
}

/** Verbo iniziale della domanda → scritta del pulsante (così si legge cosa succede, non solo «Conferma»). */
const OK_LABEL: Record<string, string> = {
  Togliere: 'Togli',
  Creare: 'Crea',
  Emettere: 'Emetti',
  Segnare: 'Segna',
  Segnalare: 'Segnala',
  Chiudere: 'Chiudi',
  Inviare: 'Invia',
  Generare: 'Genera',
  Riaprire: 'Riapri',
  Spostare: 'Sposta',
};

/** Chiede conferma: true se l'utente conferma. Titolo e pulsanti si deducono dal verbo iniziale. */
export function ask(message: string, opts: AskOptions = {}): Promise<boolean> {
  const verb = /^(\p{Lu}\p{Ll}+re)\b/u.exec(message)?.[1];
  const auto =
    verb === 'Eliminare'
      ? { title: 'Conferma eliminazione', ok: 'Elimina', cancel: 'Annulla', danger: true }
      : verb === 'Scollegare'
        ? { title: 'Conferma scollegamento', ok: 'Scollega', cancel: 'Annulla', danger: true }
        : verb === 'Annullare'
          ? { title: 'Annullare la prenotazione?', ok: 'Sì, annulla', cancel: 'Indietro', danger: true }
          : verb === 'Togliere'
            ? { title: 'Conferma', ok: 'Togli', cancel: 'Annulla', danger: true }
            : { title: 'Conferma', ok: (verb && OK_LABEL[verb]) || 'Conferma', cancel: 'Annulla', danger: false };
  return new Promise((resolve) => {
    const req: Req = { kind: 'confirm', message, ...auto, ...opts, resolve };
    if (push) push(req);
    else queue.push(req);
  });
}

/** Chiede un testo: null se l'utente annulla. */
export function askText(title: string, value = '', message = ''): Promise<string | null> {
  return new Promise((resolve) => {
    const req: Req = { kind: 'prompt', title, message, value, resolve };
    if (push) push(req);
    else queue.push(req);
  });
}

export function DialogHost() {
  const [items, setItems] = useState<Req[]>([]);
  useEffect(() => {
    push = (r) => setItems((l) => [...l, r]);
    if (queue.length) setItems((l) => [...l, ...queue.splice(0)]);
    return () => {
      push = null;
    };
  }, []);
  const cur = items[0];
  if (!cur) return null;
  const done = () => setItems((l) => l.slice(1));
  return cur.kind === 'confirm' ? (
    <ConfirmBox key={items.length} req={cur} onDone={done} />
  ) : (
    <PromptBox key={items.length} req={cur} onDone={done} />
  );
}

function ConfirmBox({ req, onDone }: { req: Extract<Req, { kind: 'confirm' }>; onDone: () => void }) {
  const okBtn = useRef<HTMLButtonElement>(null);
  useEffect(() => okBtn.current?.focus(), []);
  const close = (v: boolean) => {
    req.resolve(v);
    onDone();
  };
  return (
    <Modal
      narrow
      title={req.title}
      onClose={() => close(false)}
      footer={
        <>
          <button type="button" className="btn" onClick={() => close(false)}>
            {req.cancel}
          </button>
          <button type="button" ref={okBtn} className={`btn ${req.danger ? 'danger' : 'dark'}`} onClick={() => close(true)}>
            {req.ok}
          </button>
        </>
      }
    >
      <p className="dialog-text">{req.message}</p>
    </Modal>
  );
}

function PromptBox({ req, onDone }: { req: Extract<Req, { kind: 'prompt' }>; onDone: () => void }) {
  const [value, setValue] = useState(req.value);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    input.current?.focus();
    input.current?.select();
  }, []);
  const close = (v: string | null) => {
    req.resolve(v);
    onDone();
  };
  return (
    <Modal
      narrow
      title={req.title}
      onClose={() => close(null)}
      footer={
        <>
          <button type="button" className="btn" onClick={() => close(null)}>
            Annulla
          </button>
          <button type="button" className="btn dark" onClick={() => close(value)}>
            Conferma
          </button>
        </>
      }
    >
      {req.message && <p className="dialog-text">{req.message}</p>}
      <input
        ref={input}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') close(value);
        }}
        style={{ width: '100%', marginBottom: 12 }}
      />
    </Modal>
  );
}
